import { FC, useEffect, useState, useContext, useRef } from "react";
import moment from "moment";
import { Table, Modal, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import { useModalAction } from "../../components/modal-views/context";
import { PlusIcon } from "src/components/icons/plus-icon";
import { EditIcon } from "src/components/icons/edit-icon";
import { ResetIcon } from "src/components/icons/reset-icon";
import { EyeIcon } from "src/components/icons/eye-icon";
import { SpinnerIcon } from "src/components/icons/spinner-icon";
import Button from "src/components/ui/button";
import ToggleSwitch from "src/components/ui/switch";
import SearchFilter_1 from "src/components/search-filters/user-management-filter_1";
import client from "src/api";
import {
  ChannelPartnerApiItem,
  CreateChannelPartnerInput,
  UpdateChannelPartnerInput,
} from "src/api/types";
import GlobalContext from "src/context/global-context";
import { capitalizeFirstLetter, getErrorMessage } from "src/lib/utils";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  LIST_PAGE_SIZE,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";

const getLogoUrl = (logoPath?: string) => {
  if (!logoPath) return "";
  if (
    logoPath.startsWith("http://") ||
    logoPath.startsWith("https://") ||
    logoPath.startsWith("blob:") ||
    logoPath.startsWith("data:")
  ) {
    return logoPath;
  }
  const s3Base =
    process.env.REACT_APP_S3_URL ||
    "";
  const baseWithSlash = s3Base.endsWith("/") ? s3Base : `${s3Base}/`;
  return `${baseWithSlash}${logoPath.replace(/^\//, "")}`;
};

const ManageChannelPartnersPage: FC = () => {
  const { canView, canList, canAdd, canEdit, canDelete } = usePermissions(
    permissionConstants.MANAGE_CHANNEL_PARTNERS
  );
  const canViewChannelPartners = canView || canList;

  const { openModal } = useModalAction();
  const { setAlertText, setIsAlertOpened } = useContext(GlobalContext);

  // Table State
  const [channelPartners, setChannelPartners] = useState<ChannelPartnerApiItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Status toggle state
  const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);

  // Create / Edit Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ChannelPartnerApiItem | null>(null);
  const [name, setName] = useState("");
  const [adminDomain, setAdminDomain] = useState("");
  const [userDomain, setUserDomain] = useState("");
  const [serverIp, setServerIp] = useState("");
  const [country, setCountry] = useState("");
  const [logo, setLogo] = useState("");
  const [imagePreview, setImagePreview] = useState<string | undefined>("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const [status, setStatus] = useState("active");
  const [submitLoading, setSubmitLoading] = useState(false);

  // Details View Modal State
  const [detailsModalItem, setDetailsModalItem] = useState<ChannelPartnerApiItem | null>(null);

  // Debounce search input and reset page to 1
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchChannelPartners = async (page: number, limit: number, search?: string) => {
    setLoading(true);
    try {
      const res = await client.permissions.getChannelPartners(page, limit, search, "name");
      const safeData = Array.isArray(res.data) ? res.data : [];
      setChannelPartners(safeData);
      setTotalItems(res.total || safeData.length);
    } catch (err: any) {
      setChannelPartners([]);
      setTotalItems(0);
      const errMsg = getErrorMessage(err?.response?.data || err?.data || err);
      if (err?.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        setAlertText(errMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canViewChannelPartners) {
      fetchChannelPartners(currentPage, pageSize, debouncedSearch);
    }
  }, [currentPage, pageSize, debouncedSearch, canViewChannelPartners]);

  const onPageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  const handleOpenCreateModal = () => {
    if (!canAdd) return;
    setEditingItem(null);
    setName("");
    setAdminDomain("");
    setUserDomain("");
    setServerIp("");
    setCountry("");
    setLogo("");
    setImagePreview("");
    setUploadingLogo(false);
    setStatus("active");
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item: ChannelPartnerApiItem) => {
    if (!canEdit) return;
    setEditingItem(item);
    setName(item.name || "");
    setAdminDomain(item.admin_domain || item.domain || "");
    setUserDomain(item.user_domain || "");
    setServerIp(item.server_ip || "");
    setCountry(item.country || "");
    setLogo(item.logo || "");
    setImagePreview(item.logo ? getLogoUrl(item.logo) : "");
    setUploadingLogo(false);
    setStatus(item.status || "active");
    setIsModalOpen(true);
  };

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Instant local preview
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);

    setUploadingLogo(true);
    try {
      const uploadFd = new FormData();
      uploadFd.append("file", file);
      const uploadRes: any = await client.whiteLabelApis.UploadImageWhiteLabel(uploadFd);

      const filePath =
        uploadRes?.data?.filePath ||
        uploadRes?.filePath ||
        uploadRes?.data?.url ||
        uploadRes?.url ||
        (typeof uploadRes === "string" ? uploadRes : "");

      const baseUrl =
        uploadRes?.data?.baseUrl ||
        process.env.REACT_APP_S3_URL ||
        ""

      if (filePath) {
        setLogo(filePath);
        const formattedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
        const fullImageUrl =
          filePath.startsWith("http://") || filePath.startsWith("https://")
            ? filePath
            : `${formattedBase}${filePath.replace(/^\//, "")}`;
        setImagePreview(fullImageUrl);
      } else {
        setAlertText("Failed to retrieve uploaded image path.");
        setIsAlertOpened(true);
      }
    } catch (err: any) {
      if (err.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (err.response?.data) {
        setAlertText(getErrorMessage(err.response.data));
      } else {
        setAlertText("Failed to upload image. Please try again.");
      }
      setIsAlertOpened(true);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleToggleStatus = async (item: ChannelPartnerApiItem, newIsActive: boolean) => {
    if (!canEdit) return;
    setUpdatingStatusId(item.id);
    const newStatusString = newIsActive ? "active" : "inactive";
    try {
      const res = await client.permissions.updateChannelPartner(item.id, {
        status: newStatusString,
      });

      if (res?.status === "failure" || (res?.code && res.code >= 400)) {
        setAlertText(getErrorMessage(res));
        setIsAlertOpened(true);
        return;
      }

      setChannelPartners((prev) =>
        prev.map((cp) => (cp.id === item.id ? { ...cp, status: newStatusString } : cp))
      );
      setAlertText(`Channel Partner "${item.name}" status updated to ${newStatusString}!`);
      setIsAlertOpened(true);
    } catch (error: any) {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (error.response?.data) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setUpdatingStatusId(null);
    }
  };


  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingItem && !canEdit) return;
    if (!editingItem && !canAdd) return;

    if (!name.trim()) {
      setAlertText("error_Please enter channel partner name!");
      setIsAlertOpened(true);
      return;
    }

    if (!adminDomain.trim()) {
      setAlertText("error_Please enter admin domain URL!");
      setIsAlertOpened(true);
      return;
    }

    if (!userDomain.trim()) {
      setAlertText("error_Please enter user domain URL!");
      setIsAlertOpened(true);
      return;
    }

    if (!serverIp.trim()) {
      setAlertText("error_Please enter server IP!");
      setIsAlertOpened(true);
      return;
    }

    if (!country.trim()) {
      setAlertText("error_Please enter country!");
      setIsAlertOpened(true);
      return;
    }

    if (!logo.trim()) {
      setAlertText("error_Please upload a logo!");
      setIsAlertOpened(true);
      return;
    }

    setSubmitLoading(true);

    const logoValue = logo.trim();

    try {
      if (editingItem) {
        const updatePayload: UpdateChannelPartnerInput = {
          name: name.trim(),
          admin_domain: adminDomain.trim(),
          user_domain: userDomain.trim(),
          server_ip: serverIp.trim(),
          country: country.trim(),
          status: status,
          logo: logoValue,
        };

        const res = await client.permissions.updateChannelPartner(editingItem.id, updatePayload);
        if (res?.status === "failure" || (res?.code && res.code >= 400)) {
          setAlertText(getErrorMessage(res));
          setIsAlertOpened(true);
          return;
        }

        setAlertText(`Channel Partner "${name.trim()}" updated successfully!`);
        setIsAlertOpened(true);
      } else {
        const createPayload: CreateChannelPartnerInput = {
          name: name.trim(),
          admin_domain: adminDomain.trim(),
          user_domain: userDomain.trim(),
          server_ip: serverIp.trim(),
          country: country.trim(),
          logo: logoValue,
        };

        const res = await client.permissions.createChannelPartner(createPayload);
        if (res?.status === "failure" || (res?.code && res.code >= 400)) {
          setAlertText(getErrorMessage(res));
          setIsAlertOpened(true);
          return;
        }

        setAlertText(`Channel Partner "${name.trim()}" created successfully!`);
        setIsAlertOpened(true);
      }

      setIsModalOpen(false);
      setName("");
      setAdminDomain("");
      setUserDomain("");
      setServerIp("");
      setCountry("");
      setLogo("");
      setImagePreview("");
      setUploadingLogo(false);
      setStatus("active");
      setEditingItem(null);
      fetchChannelPartners(currentPage, pageSize, debouncedSearch);
    } catch (error: any) {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (error.response?.data) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = (item: ChannelPartnerApiItem) => {
    if (!canDelete) return;
    openModal("GENERAL_DELETE", {
      callback: async () => {
        try {
          await client.permissions.deleteChannelPartner(item.id);
          setAlertText(`Channel Partner "${item.name}" deleted successfully.`);
          setIsAlertOpened(true);
          fetchChannelPartners(currentPage, pageSize, debouncedSearch);
        } catch (error: any) {
          if (error.code === "ERR_NETWORK") {
            setAlertText(NetworkDisconnectMsg);
          } else if (error.response?.data) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
          setIsAlertOpened(true);
        }
      },
      isLoading: () => false,
    });
  };

  const columns: ColumnsType<ChannelPartnerApiItem> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      key: "id",
      className: "w-auto sm:w-[80px]",
      render: (value: number) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Partner Name
        </p>
      ),
      dataIndex: "name",
      key: "name",
      className: "w-auto min-w-[160px]",
      render: (value: string, record: ChannelPartnerApiItem) => (
        <div className="flex flex-row items-center gap-2.5">
          {record.logo ? (
            <img
              src={getLogoUrl(record.logo)}
              alt=""
              className="w-[32px] h-[32px] rounded-full object-cover border border-border flex-shrink-0"
              onError={(e) => {
                (e.target as HTMLElement).style.display = "none";
              }}
            />
          ) : (
            <></>
          )}
          <div className="flex flex-col">
            <p className="font-manrope text-primary text-[14px] font-medium">
              {capitalizeFirstLetter(value || "")}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Admin Domain
        </p>
      ),
      dataIndex: "admin_domain",
      key: "admin_domain",
      className: "w-auto min-w-[180px]",
      render: (value: string, record: ChannelPartnerApiItem) => {
        const displayVal = value || record.domain || "-";
        return (
          <div className="flex flex-row items-center gap-1.5">
            <p className="font-manrope text-primary text-[14px] font-medium break-all" title={displayVal}>
              {displayVal}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          User Domain
        </p>
      ),
      dataIndex: "user_domain",
      key: "user_domain",
      className: "w-auto min-w-[180px]",
      render: (value: string) => (
        <div className="flex flex-row items-center gap-1.5">
          <p className="font-manrope text-primary text-[14px] font-medium break-all" title={value}>
            {value || "-"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Server IP
        </p>
      ),
      dataIndex: "server_ip",
      key: "server_ip",
      className: "w-auto min-w-[120px]",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[14px] font-medium">
          {value || "-"}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Country
        </p>
      ),
      dataIndex: "country",
      key: "country",
      className: "w-auto sm:w-[120px]",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[14px] font-medium">
          {value ? capitalizeFirstLetter(value) : "-"}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Status
        </p>
      ),
      dataIndex: "status",
      key: "status",
      className: "w-[120px]",
      render: (val: string | undefined, record: ChannelPartnerApiItem) => {
        const isActive = val?.toLowerCase() === "active" || val === "1" || (val as any) === true;
        const isDisabled = !canEdit || updatingStatusId === record.id;
        return (
          <div
            className={`flex flex-row gap-2 items-center ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            title={!canEdit ? "Read only - No edit permission" : undefined}
          >
            <ToggleSwitch
              type={`${record.id}`}
              value={isActive}
              disabled={isDisabled}
              onValueChanged={(_: any, newValue: boolean) => {
                if (!canEdit) return;
                handleToggleStatus(record, newValue);
              }}
            />
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created At
        </p>
      ),
      dataIndex: "created_at",
      key: "created_at",
      className: "w-auto min-w-[170px]",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[14px] font-medium whitespace-nowrap">
          {value ? moment.utc(value).local().format("YYYY-MM-DD HH:mm:ss") : "-"}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      key: "actions",
      className: "w-[120px]",
      render: (_, record) => {
        if (!canEdit && !canView) {
          return (
            <p className="font-manrope text-gray-400 text-[20px] font-bold text-center">
              —
            </p>
          );
        }
        return (
          <div className="flex flex-row items-center gap-1">
            {canEdit && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-center hover:bg-gray rounded"
                onClick={() => handleOpenEditModal(record)}
                title="Edit Channel Partner"
              >
                <EditIcon className="w-[18px] h-[18px] text-primary" />
              </button>
            )}
            {canView && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-center hover:bg-gray rounded"
                onClick={() => setDetailsModalItem(record)}
                title="View Partner Details"
              >
                <EyeIcon className="w-[18px] h-[18px] text-primary" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  if (!canViewChannelPartners) {
    return (
      <div className="w-full h-full bg-gray">
        <div className="w-full h-full flex-col p-2 sm:p-4">
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
            Manage Channel Partners
          </p>
          <div className="bg-white rounded-[12px] p-6 mt-4 text-center text-secondary">
            You do not have permission to view channel partners.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full h-full flex-col p-2 sm:p-4">
        {/* Header */}
        <div className="w-full flex flex-row items-center justify-between">
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
            Manage Channel Partners
          </p>
          <PermissionGate module={[permissionConstants.MANAGE_CHANNEL_PARTNERS]} action="can_add">
            <div className="flex flex-row items-center gap-3">
              <button
                className="flex flex-row items-center justify-between px-2 sm:px-4 py-2 gap-2 bg-white rounded-[6px] flex-shrink-0 hover:bg-[#E6E8EC]"
                onClick={handleOpenCreateModal}
              >
                <p className="font-manrope text-[14px] font-medium text-primary">
                  Add Channel Partner
                </p>
                <PlusIcon className="w-[18px] h-[18px] text-primary" />
              </button>
            </div>
          </PermissionGate>
        </div>

        {/* Search & Actions Toolbar */}
        <div className="mt-2 sm:mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <SearchFilter_1
            options={[
              { code: "name", name: "Name" },
              { code: "slug", name: "Slug" },
              { code: "country", name: "Country" },
            ]}
            placeholder="Search channel partner by name..."
            value="name"
            onChange={() => { }}
            searchText={searchQuery}
            setSearchText={setSearchQuery}
          />

          <div className="flex items-center gap-2">
            <button
              className="flex flex-row items-center justify-between px-3 py-2 gap-2 bg-white rounded-[6px] flex-shrink-0 hover:bg-[#E6E8EC] h-[44px] border border-light-300"
              onClick={() => fetchChannelPartners(currentPage, pageSize, debouncedSearch)}
            >
              <p className="font-manrope text-[14px] font-medium text-primary">
                Refresh
              </p>
              <ResetIcon className="w-[16px] h-[16px] text-primary" />
            </button>
          </div>
        </div>

        {/* Channel Partners Table */}
        <div className="flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table
            loading={loading}
            showSorterTooltip={false}
            columns={columns}
            dataSource={channelPartners}
            pagination={{
              total: totalItems,
              current: currentPage,
              pageSize: pageSize,
              onChange: onPageChange,
              position: ["bottomCenter"],
              defaultPageSize: LIST_PAGE_SIZE,
              showSizeChanger: false,
            }}
            rowKey="id"
          />
        </div>
      </div>

      {/* Create / Edit Channel Partner Modal */}
      <Modal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingItem(null);
        }}
        footer={null}
        title={
          <p className="text-[20px] font-manrope font-medium text-primary">
            {editingItem ? "Edit Channel Partner" : "Add New Channel Partner"}
          </p>
        }
        width={640}
      >
        <form onSubmit={handleFormSubmit} className="mt-4 flex flex-col gap-3 font-manrope">
          <div>
            <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
              Partner Name <span className="text-error">*</span>
            </p>
            <input
              className="h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2 text-primary focus:border-active focus:ring-0"
              placeholder="e.g. Fastoo Bitanica"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
                Admin Domain URL <span className="text-error">*</span>
              </p>
              <input
                className="h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2 text-primary focus:border-active focus:ring-0"
                placeholder="e.g. https://staging-services-api.bitanica.com"
                value={adminDomain}
                onChange={(e) => setAdminDomain(e.target.value)}
              />
            </div>

            <div>
              <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
                User Domain URL <span className="text-error">*</span>
              </p>
              <input
                className="h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2 text-primary focus:border-active focus:ring-0"
                placeholder="e.g. https://staging-services-api.bitanica.com"
                value={userDomain}
                onChange={(e) => setUserDomain(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
                Server IP <span className="text-error">*</span>
              </p>
              <input
                className="h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2 text-primary focus:border-active focus:ring-0"
                placeholder="e.g. 3.151.40.37"
                value={serverIp}
                onChange={(e) => setServerIp(e.target.value)}
              />
            </div>

            <div>
              <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
                Country <span className="text-error">*</span>
              </p>
              <input
                className="h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2 text-primary focus:border-active focus:ring-0"
                placeholder="e.g. Georgia"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
            <div>
              <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
                Logo <span className="text-error">*</span>
              </p>
              <div className="border-[2px] relative border-dashed h-[90px] w-[90px] border-[#9e9e9e] rounded-full mt-1 bg-[#f0f0f0] overflow-hidden flex items-center justify-center">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  id="partnerLogoFile"
                  className="w-full h-full absolute top-0 left-0 z-10 cursor-pointer opacity-0"
                  disabled={uploadingLogo}
                  onChange={handleLogoFileChange}
                />

                {uploadingLogo ? (
                  <div className="flex flex-col items-center justify-center w-full h-full bg-black/5">
                    <SpinnerIcon className="w-6 h-6 animate-spin text-primary" />
                    <span className="text-[9px] font-manrope text-secondary mt-0.5">Uploading...</span>
                  </div>
                ) : imagePreview ? (
                  <div className="flex items-center justify-center h-full w-full relative">
                    <img
                      src={imagePreview}
                      alt="Logo"
                      className="max-w-full h-full w-full object-cover rounded-full"
                    />
                    <EditIcon className="w-[18px] h-[18px] text-primary absolute right-[2px] bottom-[2px] bg-white rounded-full p-0.5 shadow pointer-events-none" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full flex-col cursor-pointer pointer-events-none text-secondary">
                    <div className="text-center h-[38px] flex items-center justify-center">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17 12C17 13.7 15.7 15 14 15C12.3 15 11 13.7 11 12C11 10.3 12.3 9 14 9C15.7 9 17 10.3 17 12ZM22 7V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V7C2 5.9 2.9 5 4 5V4H8V5H10L11 3H17L18 5H20C21.1 5 22 5.9 22 7ZM7.5 9C7.5 8.2 6.8 7.5 6 7.5C5.2 7.5 4.5 8.2 4.5 9C4.5 9.8 5.2 10.5 6 10.5C6.8 10.5 7.5 9.8 7.5 9ZM19 12C19 9.2 16.8 7 14 7C11.2 7 9 9.2 9 12C9 14.8 11.2 17 14 17C16.8 17 19 14.8 19 12Z"
                          fill="black"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {editingItem && (
              <div>
                <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
                  Status
                </p>
                <Select
                  size="large"
                  value={status}
                  onChange={(val) => setStatus(val)}
                  className="w-full font-manrope h-[48px]"
                  options={[
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                />
              </div>
            )}
          </div>


          <div className="w-full flex flex-row items-center justify-end gap-2 mt-4 pt-4 border-t border-light-300">
            <Button
              variant="solid"
              type="button"
              className="w-[120px] rounded-[8px] font-manrope text-[14px] font-bold leading-[20px] h-[44px] bg-[#BCBEAD] hover:bg-light-600"
              onClick={() => {
                setIsModalOpen(false);
                setEditingItem(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="validate"
              type="submit"
              className="w-[120px] rounded-[8px] font-manrope text-[14px] font-bold leading-[20px] h-[44px]"
              disabled={submitLoading || uploadingLogo}
              isLoading={submitLoading}
            >
              {editingItem ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* View Partner Details Modal */}
      <Modal
        open={Boolean(detailsModalItem)}
        onCancel={() => setDetailsModalItem(null)}
        footer={null}
        title={
          <p className="text-[20px] font-manrope font-medium text-primary">
            Partner Details: {detailsModalItem?.name}
          </p>
        }
        width={560}
      >
        {detailsModalItem && (
          <div className="mt-4 flex flex-col gap-3 font-manrope">
            <div className="p-3 rounded-[8px] bg-gray border border-border flex flex-col gap-2">
              {detailsModalItem.logo && (
                <div className="flex justify-between items-center py-1 border-b border-light-300">
                  <span className="text-[13px] text-secondary font-medium">Logo:</span>
                  <img
                    src={getLogoUrl(detailsModalItem.logo)}
                    alt="Partner Logo"
                    className="w-9 h-9 rounded-full object-cover border border-border"
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = "none";
                    }}
                  />
                </div>
              )}
              <div className="flex justify-between items-center py-1 border-b border-light-300">
                <span className="text-[13px] text-secondary font-medium">Partner Name:</span>
                <span className="text-[14px] font-medium text-primary">{detailsModalItem.name}</span>
              </div>
              {detailsModalItem.slug && (
                <div className="flex justify-between items-center py-1 border-b border-light-300">
                  <span className="text-[13px] text-secondary font-medium">Slug:</span>
                  <span className="text-[13px] font-mono text-primary">{detailsModalItem.slug}</span>
                </div>
              )}
              <div className="flex justify-between items-center py-1 border-b border-light-300">
                <span className="text-[13px] text-secondary font-medium">Admin Domain:</span>
                <span className="text-[13px] font-medium text-primary break-all">{detailsModalItem.admin_domain || detailsModalItem.domain || "-"}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-light-300">
                <span className="text-[13px] text-secondary font-medium">User Domain:</span>
                <span className="text-[13px] font-medium text-primary break-all">{detailsModalItem.user_domain || "-"}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-light-300">
                <span className="text-[13px] text-secondary font-medium">Server IP:</span>
                <span className="text-[13px] font-mono text-primary">{detailsModalItem.server_ip || "-"}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-light-300">
                <span className="text-[13px] text-secondary font-medium">Country:</span>
                <span className="text-[13px] font-medium text-primary">{detailsModalItem.country || "-"}</span>
              </div>
              {detailsModalItem.created_at && (
                <div className="flex justify-between items-center py-1 border-b border-light-300">
                  <span className="text-[13px] text-secondary font-medium">Created At:</span>
                  <span className="text-[13px] font-medium text-primary">
                    {moment.utc(detailsModalItem.created_at).local().format("YYYY-MM-DD HH:mm:ss")}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center py-1">
                <span className="text-[13px] text-secondary font-medium">Status:</span>
                <span className={`px-2 py-0.5 rounded text-[12px] font-medium ${detailsModalItem.status === "active" ? "bg-[#e6f7ec] text-success border border-[#b7ebc7]" : "bg-gray text-secondary border border-light-300"}`}>
                  {capitalizeFirstLetter(detailsModalItem.status || "active")}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ManageChannelPartnersPage;
